import {
  getCreateQuestionsBreadcrumbLabel,
  getCreateQuestionsNavLabel,
} from "../data/copy/usClassroomTerms";
import { TOOL_DISPLAY_NAMES } from "../data/tools/toolDisplayNames";
import {
  TEMA_PERMISSIONS,
  TEMA_ROUTE_IDS,
  TEMA_ROUTE_PATHS,
} from "./temaRouteConstants";
import {
  LazyActivityGeneratorPage,
  LazyConceptQuestionsPage,
  LazyContentSimplifierPage,
  LazyDashboardPage,
  LazyDifficultyBalancerPage,
  LazyDoubtSolverPage,
  LazyExplainDifferentlyPage,
  LazyLibraryPage,
  LazyBillingPage,
  LazyCertificationPage,
  LazyAifeVerifyPage,
  LazyTeacherOnboardingPage,
  LazyProjectBuilderPage,
  LazyQuestionPaperPage,
  LazyQuestionVariationsPage,
  LazyQuickAssessmentPage,
  LazyQuickSheetsPage,
  LazyRealWorldTaskGeneratorPage,
  LazySectionBuilderPage,
  LazySectionHubPage,
  LazySlideDeckGeneratorPage,
  LazyTeachThisTopicPage,
} from "./lazyPages";

const WORKSPACE_PERMISSIONS = [
  TEMA_PERMISSIONS.WORKSPACE_ACCESS,
  TEMA_PERMISSIONS.TOOLS_GENERATE,
];

const createQuestionsBreadcrumbLabel = getCreateQuestionsBreadcrumbLabel();

const SECTION_HUB_PATHS = {
  [createQuestionsBreadcrumbLabel]: TEMA_ROUTE_PATHS.CREATE_QUESTIONS,
  "Teach and Prepare": TEMA_ROUTE_PATHS.TEACH_PREPARE,
  "Build Assessments": TEMA_ROUTE_PATHS.BUILD_ASSESSMENTS,
  "Assign Enrichment": TEMA_ROUTE_PATHS.ASSIGN_ENRICHMENT,
};

function sectionHubRoute({ id, path, label, documentTitle }) {
  return {
    id,
    path,
    component: LazySectionHubPage,
    module: "tema",
    requiresAuth: true,
    permissions: [TEMA_PERMISSIONS.WORKSPACE_ACCESS],
    workspace: false,
    lazy: true,
    documentTitle,
    breadcrumbs: [
      { label: "iTEMA", href: TEMA_ROUTE_PATHS.DASHBOARD },
      { label },
    ],
  };
}

function workspaceRoute({
  id,
  path,
  aliases = [],
  component,
  label,
  section,
  documentTitle,
}) {
  return {
    id,
    path,
    aliases,
    component,
    module: "tema",
    requiresAuth: true,
    permissions: WORKSPACE_PERMISSIONS,
    workspace: true,
    lazy: true,
    documentTitle,
    breadcrumbs: [
      { label: "iTEMA", href: TEMA_ROUTE_PATHS.DASHBOARD },
      ...(section
        ? [
            {
              label: section,
              ...(SECTION_HUB_PATHS[section]
                ? { href: SECTION_HUB_PATHS[section] }
                : {}),
            },
          ]
        : []),
      { label },
    ],
  };
}

export const TEMA_ROUTES = [
  {
    id: TEMA_ROUTE_IDS.DASHBOARD,
    path: TEMA_ROUTE_PATHS.DASHBOARD,
    aliases: ["#dashboard"],
    component: LazyDashboardPage,
    module: "tema",
    requiresAuth: true,
    permissions: [TEMA_PERMISSIONS.WORKSPACE_ACCESS],
    workspace: false,
    lazy: true,
    documentTitle: "iTEMA",
    breadcrumbs: [{ label: "Dashboard" }],
  },
  {
    id: TEMA_ROUTE_IDS.ONBOARDING,
    path: TEMA_ROUTE_PATHS.ONBOARDING,
    component: LazyTeacherOnboardingPage,
    module: "tema",
    requiresAuth: true,
    permissions: [TEMA_PERMISSIONS.WORKSPACE_ACCESS],
    workspace: false,
    lazy: true,
    documentTitle: "iTEMA — Onboarding",
    breadcrumbs: [
      { label: "iTEMA", href: TEMA_ROUTE_PATHS.DASHBOARD },
      { label: "Onboarding" },
    ],
  },
  sectionHubRoute({
    id: TEMA_ROUTE_IDS.TEACH_PREPARE,
    path: TEMA_ROUTE_PATHS.TEACH_PREPARE,
    label: "Teach & prepare",
    documentTitle: "iTEMA",
  }),
  sectionHubRoute({
    id: TEMA_ROUTE_IDS.CREATE_QUESTIONS,
    path: TEMA_ROUTE_PATHS.CREATE_QUESTIONS,
    label: getCreateQuestionsNavLabel(),
    documentTitle: "iTEMA",
  }),
  sectionHubRoute({
    id: TEMA_ROUTE_IDS.BUILD_ASSESSMENTS,
    path: TEMA_ROUTE_PATHS.BUILD_ASSESSMENTS,
    label: "Build Assessments",
    documentTitle: "iTEMA",
  }),
  sectionHubRoute({
    id: TEMA_ROUTE_IDS.ASSIGN_ENRICHMENT,
    path: TEMA_ROUTE_PATHS.ASSIGN_ENRICHMENT,
    label: "Assign Enrichment",
    documentTitle: "iTEMA",
  }),
  workspaceRoute({
    id: TEMA_ROUTE_IDS.QUICK_SHEETS,
    path: TEMA_ROUTE_PATHS.QUICK_SHEETS,
    component: LazyQuickSheetsPage,
    label: TOOL_DISPLAY_NAMES["quick-sheets"],
    section: createQuestionsBreadcrumbLabel,
    documentTitle: "iTEMA",
  }),
  workspaceRoute({
    id: TEMA_ROUTE_IDS.CONCEPT_QUESTIONS,
    path: TEMA_ROUTE_PATHS.CONCEPT_QUESTIONS,
    component: LazyConceptQuestionsPage,
    label: TOOL_DISPLAY_NAMES["concept-questions"],
    section: createQuestionsBreadcrumbLabel,
    documentTitle: "iTEMA",
  }),
  workspaceRoute({
    id: TEMA_ROUTE_IDS.QUESTION_VARIATIONS,
    path: TEMA_ROUTE_PATHS.QUESTION_VARIATIONS,
    component: LazyQuestionVariationsPage,
    label: "Question Variations",
    section: createQuestionsBreadcrumbLabel,
    documentTitle: "iTEMA",
  }),
  workspaceRoute({
    id: TEMA_ROUTE_IDS.QUICK_ASSESSMENT,
    path: TEMA_ROUTE_PATHS.QUICK_ASSESSMENT,
    component: LazyQuickAssessmentPage,
    label: TOOL_DISPLAY_NAMES["quick-assessment"],
    section: createQuestionsBreadcrumbLabel,
    documentTitle: "iTEMA",
  }),
  workspaceRoute({
    id: TEMA_ROUTE_IDS.TEACH_THIS_TOPIC,
    path: TEMA_ROUTE_PATHS.TEACH_THIS_TOPIC,
    component: LazyTeachThisTopicPage,
    label: TOOL_DISPLAY_NAMES["teach-this-topic"],
    section: "Teach and Prepare",
    documentTitle: "iTEMA",
  }),
  workspaceRoute({
    id: TEMA_ROUTE_IDS.EXPLAIN_DIFFERENTLY,
    path: TEMA_ROUTE_PATHS.EXPLAIN_DIFFERENTLY,
    component: LazyExplainDifferentlyPage,
    label: "Explain Differently",
    section: "Teach and Prepare",
    documentTitle: "iTEMA",
  }),
  workspaceRoute({
    id: TEMA_ROUTE_IDS.CONTENT_SIMPLIFIER,
    path: TEMA_ROUTE_PATHS.CONTENT_SIMPLIFIER,
    component: LazyContentSimplifierPage,
    label: TOOL_DISPLAY_NAMES["content-simplifier"],
    section: "Teach and Prepare",
    documentTitle: "iTEMA",
  }),
  workspaceRoute({
    id: TEMA_ROUTE_IDS.QUESTION_PAPER,
    path: TEMA_ROUTE_PATHS.QUESTION_PAPER,
    aliases: ["#question-paper"],
    component: LazyQuestionPaperPage,
    label: TOOL_DISPLAY_NAMES["question-paper"],
    section: "Build Assessments",
    documentTitle: "iTEMA",
  }),
  workspaceRoute({
    id: TEMA_ROUTE_IDS.SECTION_BUILDER,
    path: TEMA_ROUTE_PATHS.SECTION_BUILDER,
    component: LazySectionBuilderPage,
    label: "Section Builder",
    section: "Build Assessments",
    documentTitle: "iTEMA",
  }),
  workspaceRoute({
    id: TEMA_ROUTE_IDS.DIFFICULTY_BALANCER,
    path: TEMA_ROUTE_PATHS.DIFFICULTY_BALANCER,
    component: LazyDifficultyBalancerPage,
    label: TOOL_DISPLAY_NAMES["difficulty-balancer"],
    section: "Build Assessments",
    documentTitle: "iTEMA",
  }),
  workspaceRoute({
    id: TEMA_ROUTE_IDS.DOUBT_SOLVER,
    path: TEMA_ROUTE_PATHS.DOUBT_SOLVER,
    component: LazyDoubtSolverPage,
    label: TOOL_DISPLAY_NAMES["doubt-solver"],
    documentTitle: "iTEMA",
  }),
  workspaceRoute({
    id: TEMA_ROUTE_IDS.SLIDE_DECK_GENERATOR,
    path: TEMA_ROUTE_PATHS.SLIDE_DECK_GENERATOR,
    aliases: ["#slide-deck"],
    component: LazySlideDeckGeneratorPage,
    label: TOOL_DISPLAY_NAMES["slide-deck-generator"],
    documentTitle: "iTEMA",
  }),
  workspaceRoute({
    id: TEMA_ROUTE_IDS.PROJECT_BUILDER,
    path: TEMA_ROUTE_PATHS.PROJECT_BUILDER,
    component: LazyProjectBuilderPage,
    label: TOOL_DISPLAY_NAMES["project-builder"],
    section: "Assign Enrichment",
    documentTitle: "iTEMA",
  }),
  workspaceRoute({
    id: TEMA_ROUTE_IDS.ACTIVITY_GENERATOR,
    path: TEMA_ROUTE_PATHS.ACTIVITY_GENERATOR,
    component: LazyActivityGeneratorPage,
    label: TOOL_DISPLAY_NAMES["activity-generator"],
    section: "Assign Enrichment",
    documentTitle: "iTEMA",
  }),
  workspaceRoute({
    id: TEMA_ROUTE_IDS.REAL_WORLD_TASK,
    path: TEMA_ROUTE_PATHS.REAL_WORLD_TASK,
    aliases: ["#real-world-task-generator"],
    component: LazyRealWorldTaskGeneratorPage,
    label: "Real-World Task Generator",
    section: "Assign Enrichment",
    documentTitle: "iTEMA",
  }),
  {
    id: TEMA_ROUTE_IDS.LIBRARY,
    path: TEMA_ROUTE_PATHS.LIBRARY,
    aliases: ["#my-library", "#saved-lessons"],
    component: LazyLibraryPage,
    module: "tema",
    requiresAuth: true,
    permissions: [TEMA_PERMISSIONS.WORKSPACE_ACCESS],
    workspace: false,
    lazy: true,
    documentTitle: "iTEMA",
    breadcrumbs: [
      { label: "iTEMA", href: TEMA_ROUTE_PATHS.DASHBOARD },
      { label: "Saved Lessons" },
    ],
  },
  {
    id: TEMA_ROUTE_IDS.BILLING,
    path: TEMA_ROUTE_PATHS.BILLING,
    component: LazyBillingPage,
    module: "tema",
    requiresAuth: true,
    permissions: [TEMA_PERMISSIONS.WORKSPACE_ACCESS],
    workspace: false,
    lazy: true,
    documentTitle: "iTEMA — Billing & Subscription",
    breadcrumbs: [
      { label: "iTEMA", href: TEMA_ROUTE_PATHS.DASHBOARD },
      { label: "Billing & Subscriptions" },
    ],
  },
  {
    id: TEMA_ROUTE_IDS.CERTIFICATION,
    path: TEMA_ROUTE_PATHS.CERTIFICATION,
    aliases: ["#aife", "#itema-certification"],
    component: LazyCertificationPage,
    module: "tema",
    requiresAuth: true,
    permissions: [TEMA_PERMISSIONS.WORKSPACE_ACCESS],
    workspace: false,
    lazy: true,
    documentTitle: "iTEMA — AIFE Certification",
    breadcrumbs: [
      { label: "iTEMA", href: TEMA_ROUTE_PATHS.DASHBOARD },
      { label: "AIFE Certification" },
    ],
  },
  {
    id: TEMA_ROUTE_IDS.AIFE_VERIFY,
    path: TEMA_ROUTE_PATHS.AIFE_VERIFY,
    pathPrefix: "#aife-verify/",
    component: LazyAifeVerifyPage,
    module: "tema",
    requiresAuth: false,
    permissions: [],
    workspace: false,
    lazy: true,
    documentTitle: "iTEMA — Verify AIFE credential",
    breadcrumbs: [
      { label: "iTEMA", href: TEMA_ROUTE_PATHS.DASHBOARD },
      { label: "Verify AIFE credential" },
    ],
  },
];

export const TEMA_NOT_FOUND_ROUTE = {
  id: TEMA_ROUTE_IDS.NOT_FOUND,
  path: null,
  component: null,
  module: "tema",
  requiresAuth: true,
  permissions: [TEMA_PERMISSIONS.WORKSPACE_ACCESS],
  workspace: false,
  lazy: false,
  documentTitle: "iTEMA",
  breadcrumbs: [{ label: "Page not found" }],
};
